/**
 * LearningSession — Adaptive mixed mini game session.
 * 10 rounds, evaluate every 3 rounds, auto adjust level.
 * Level 1=Flashcard, 2=PictureMatch, 3=WordScramble, 4=SpeedQuiz
 * See docs/adaptive-learning-session.md
 */
import React, { useState, useCallback, useRef } from 'react';
import { playCorrect, playIncorrect, playComplete } from '../../utils/sounds';

const LEVEL_NAMES = ['', 'Flashcard', 'Picture Match', 'Word Scramble', 'Speed Quiz'];
const LEVEL_EMOJI = ['', '📚', '🖼️', '✍️', '⚡'];
const LEVEL_COLORS = ['', '#6366f1', '#06b6d4', '#8b5cf6', '#ef4444'];
const EVAL_EVERY = 3;
const MAX_ROUNDS = 20;
const ENERGY_GOAL = 100;
const ENERGY_CORRECT = 10;
const ENERGY_RETRY_CORRECT = 5;  // retry đúng chỉ bù lại, không lời thêm
const ENERGY_WRONG = -5;

const LearningSession = ({ sessionData, onComplete }) => {
  const { topic, start_level, rounds } = sessionData;

  const [idx, setIdx] = useState(0);
  const [level, setLevel] = useState(start_level);
  const [results, setResults] = useState([]);
  const [phase, setPhase] = useState('play'); // 'play' | 'feedback' | 'levelup' | 'done'
  const [lastCorrect, setLastCorrect] = useState(null);
  const [levelMsg, setLevelMsg] = useState('');
  const [energy, setEnergy] = useState(0);
  const [energyDelta, setEnergyDelta] = useState(0); // for +10 / -5 animation

  const retryQueue = useRef([]);
  const retryIndices = useRef(new Set()); // indices in roundOrder that are retries
  const [roundOrder, setRoundOrder] = useState(() => rounds.map((_, i) => i));

  const currentRoundIdx = roundOrder[idx];
  const round = currentRoundIdx !== undefined ? rounds[currentRoundIdx] : null;
  const color = LEVEL_COLORS[level] || '#6366f1';

  // Evaluate every 3 rounds
  const evaluateAndAdvance = useCallback((newResults) => {
    const justDone = newResults.length;
    if (justDone > 0 && justDone % EVAL_EVERY === 0) {
      const last3 = newResults.slice(-EVAL_EVERY);
      const correct3 = last3.filter(r => r.correct).length;

      if (correct3 === EVAL_EVERY && level < 4) {
        const newLevel = Math.min(level + 1, 4);
        setLevel(newLevel);
        setLevelMsg(`🔥 Giỏi quá! ${LEVEL_NAMES[newLevel]}!`);
        setPhase('levelup');
        setTimeout(() => setPhase('play'), 1500);
        return;
      } else if (correct3 <= 1 && level > 1) {
        const newLevel = Math.max(level - 1, 1);
        setLevel(newLevel);
        setLevelMsg(`💪 Ôn lại nhẹ nhàng hơn nhé!`);
        setPhase('levelup');
        setTimeout(() => setPhase('play'), 1500);
        return;
      }
    }
  }, [level]);

  const handleAnswer = useCallback((correct) => {
    const r = { tag_id: round.tag_id, correct, level };
    const newResults = [...results, r];
    setResults(newResults);
    setLastCorrect(correct);
    setPhase('feedback');

    // Audio feedback
    if (correct) playCorrect(); else playIncorrect();

    // Update energy — retry correct only recovers lost points
    const isRetry = retryIndices.current.has(idx);
    const delta = correct
      ? (isRetry ? ENERGY_RETRY_CORRECT : ENERGY_CORRECT)
      : ENERGY_WRONG;
    setEnergyDelta(delta);
    setEnergy(prev => Math.max(0, Math.min(ENERGY_GOAL, prev + delta)));

    // If wrong → retry queue
    if (!correct && round) {
      retryQueue.current.push({ roundIdx: currentRoundIdx });
    }

    const newEnergy = Math.max(0, Math.min(ENERGY_GOAL, energy + delta));

    setTimeout(() => {
      setEnergyDelta(0);
      const nextIdx = idx + 1;

      // Energy reached goal → WIN!
      if (newEnergy >= ENERGY_GOAL) {
        playComplete();
        setPhase('done');
        return;
      }

      // Hit max rounds → done
      if (nextIdx >= MAX_ROUNDS) {
        setPhase('done');
        return;
      }

      if (nextIdx >= roundOrder.length) {
        if (retryQueue.current.length > 0) {
          const retries = retryQueue.current.splice(0, Math.min(retryQueue.current.length, MAX_ROUNDS - roundOrder.length));
          if (retries.length > 0) {
            const startIdx = roundOrder.length;
            retries.forEach((_, i) => retryIndices.current.add(startIdx + i));
            setRoundOrder(prev => [...prev, ...retries.map(r => r.roundIdx)]);
          } else {
            setPhase('done');
            return;
          }
        } else {
          setPhase('done');
          return;
        }
      }

      setIdx(nextIdx);
      evaluateAndAdvance(newResults);
      if (phase !== 'levelup') setPhase('play');
    }, correct ? 800 : 1500);
  }, [round, results, idx, level, energy, currentRoundIdx, roundOrder, evaluateAndAdvance, phase]);

  // ═══ DONE SCREEN ═══
  if (phase === 'done' || (!round && idx > 0)) {
    const correct = results.filter(r => r.correct).length;
    const total = results.length;
    const pct = total ? Math.round((correct / total) * 100) : 0;
    const won = energy >= ENERGY_GOAL;
    const levelUps = results.reduce((acc, r, i) => {
      if (i > 0 && r.level > results[i - 1].level) return acc + 1;
      return acc;
    }, 0);
    const stars = correct * 2 + (pct >= 80 ? 5 : 0) + levelUps * 3 + (won ? 10 : 0);

    return (
      <div className="text-center py-8 space-y-5">
        <div className="text-6xl">{won ? '🏆' : pct >= 80 ? '🎉' : pct >= 50 ? '💪' : '📚'}</div>
        <h2 className="text-2xl font-black text-slate-800">
          {won ? 'Full Energy! 🎉' : pct >= 80 ? 'Tuyệt vời!' : pct >= 50 ? 'Khá tốt!' : 'Cần ôn thêm!'}
        </h2>

        {/* Final energy bar */}
        <div className="max-w-xs mx-auto">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-bold text-slate-400">Energy</span>
            <span className="text-sm font-black" style={{ color }}>⚡ {Math.round(energy)}/{ENERGY_GOAL}</span>
          </div>
          <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700"
              style={{ width: `${(energy / ENERGY_GOAL) * 100}%`, background: `linear-gradient(90deg, ${color}, ${color}aa)` }} />
          </div>
        </div>

        <div className="flex justify-center gap-3">
          <div className="bg-green-50 rounded-2xl p-4 min-w-[80px]">
            <p className="text-2xl font-black text-green-600">{correct}</p>
            <p className="text-[10px] font-bold text-green-500">Đúng</p>
          </div>
          <div className="bg-red-50 rounded-2xl p-4 min-w-[80px]">
            <p className="text-2xl font-black text-red-600">{total - correct}</p>
            <p className="text-[10px] font-bold text-red-500">Sai</p>
          </div>
          <div className="bg-amber-50 rounded-2xl p-4 min-w-[80px]">
            <p className="text-2xl font-black text-amber-600">+{stars}</p>
            <p className="text-[10px] font-bold text-amber-500">Stars</p>
          </div>
        </div>
        <button onClick={() => onComplete?.(results)}
          className="px-8 py-3 rounded-2xl text-white font-black text-lg shadow-lg active:scale-95 transition-all"
          style={{ background: `linear-gradient(135deg, ${color}, ${color}dd)` }}>
          Hoàn thành ⭐
        </button>
      </div>
    );
  }

  if (!round) return null;

  const energyPct = (energy / ENERGY_GOAL) * 100;
  const barColor = energy >= 80 ? '#22c55e' : energy >= 50 ? '#f59e0b' : color;

  // ═══ LEVEL UP ANIMATION ═══
  if (phase === 'levelup') {
    return (
      <div className="text-center py-16 space-y-4">
        <div className="text-6xl" style={{ animation: 'mqStarSpin .6s ease-out both' }}>{LEVEL_EMOJI[level]}</div>
        <p className="text-xl font-black text-slate-800">{levelMsg}</p>
        <p className="text-sm text-slate-500">Level {level}: {LEVEL_NAMES[level]}</p>
      </div>
    );
  }

  // ═══ RENDER GAME BY LEVEL ═══
  return (
    <div className="space-y-4">
      {/* Energy bar */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="text-lg">⚡</span>
            <span className="text-xs font-black text-slate-500 uppercase">Energy</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-black" style={{ color: barColor }}>{Math.round(energy)}/{ENERGY_GOAL}</span>
            {/* +/- delta animation */}
            {energyDelta !== 0 && (
              <span className={`text-xs font-black ${energyDelta > 0 ? 'text-green-500' : 'text-red-500'}`}
                style={{ animation: 'mqFadeUp .5s ease-out both' }}>
                {energyDelta > 0 ? `+${energyDelta}` : energyDelta}
              </span>
            )}
            <span className="px-2 py-0.5 rounded-full text-[10px] font-black text-white" style={{ background: color }}>
              {LEVEL_EMOJI[level]} L{level}
            </span>
          </div>
        </div>
        <div className="h-3.5 bg-slate-200 rounded-full overflow-hidden relative">
          <div className="h-full rounded-full transition-all duration-500 relative"
            style={{ width: `${energyPct}%`, background: `linear-gradient(90deg, ${barColor}, ${barColor}bb)` }}>
            {/* Glow pulse when near goal */}
            {energy >= 80 && (
              <div className="absolute inset-0 rounded-full"
                style={{ background: `${barColor}40`, animation: 'mqPulse 1.5s ease-in-out infinite' }} />
            )}
          </div>
          {/* Goal marker */}
          <div className="absolute right-0 top-0 bottom-0 w-1 bg-slate-400 rounded" />
        </div>
        {energy >= 80 && energy < ENERGY_GOAL && (
          <p className="text-[10px] font-bold text-green-500 mt-1 text-center">🔥 Sắp full energy rồi!</p>
        )}
      </div>

      {/* Topic name */}
      <div className="text-center">
        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{topic.name}</span>
      </div>

      {/* Game content based on level — key forces remount on round change */}
      {level === 1 && <FlashcardRound key={`fc-${idx}`} round={round} onAnswer={handleAnswer} disabled={phase === 'feedback'} />}
      {level === 2 && <PictureMatchRound key={`pm-${idx}`} round={round} onAnswer={handleAnswer} disabled={phase === 'feedback'} />}
      {level === 3 && <WordScrambleRound key={`ws-${idx}`} round={round} onAnswer={handleAnswer} disabled={phase === 'feedback'} />}
      {level === 4 && <SpeedQuizRound key={`sq-${idx}`} round={round} onAnswer={handleAnswer} disabled={phase === 'feedback'} />}

      {/* Feedback overlay */}
      {phase === 'feedback' && lastCorrect !== null && (
        <div className={`text-center p-3 rounded-xl ${lastCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
          <span className="text-2xl">{lastCorrect ? '✓' : '✗'}</span>
          <span className={`ml-2 font-bold ${lastCorrect ? 'text-green-600' : 'text-red-600'}`}>
            {lastCorrect ? 'Đúng rồi!' : `Đáp án: ${round.flashcard.name_vi}`}
          </span>
        </div>
      )}
    </div>
  );
};

// ═══ INDIVIDUAL ROUND COMPONENTS (simplified, inline) ═══

const FlashcardRound = ({ round, onAnswer, disabled }) => {
  const [flipped, setFlipped] = useState(false);
  const d = round.flashcard;
  return (
    <div className="space-y-4">
      <div onClick={() => !disabled && setFlipped(!flipped)}
        className="cursor-pointer rounded-2xl p-8 text-center shadow-md min-h-[200px] flex flex-col items-center justify-center transition-all"
        style={{ background: flipped ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)' : 'linear-gradient(135deg, #eef2ff, #e0e7ff)' }}>
        {!flipped ? (
          <>
            <p className="text-3xl font-black text-slate-800 mb-2">{round.word}</p>
            <p className="text-sm text-slate-400">Tap to flip</p>
          </>
        ) : (
          <>
            <p className="text-2xl font-black text-slate-800 mb-2">{d.name_vi}</p>
            {d.definition_vi && <p className="text-sm text-slate-600">{d.definition_vi}</p>}
          </>
        )}
      </div>
      {flipped && !disabled && (
        <div className="flex gap-3 justify-center">
          <button onClick={() => onAnswer(false)}
            className="flex-1 py-3 rounded-xl bg-red-50 text-red-600 font-black active:scale-95 transition-all">
            ✗ Chưa biết
          </button>
          <button onClick={() => onAnswer(true)}
            className="flex-1 py-3 rounded-xl bg-green-50 text-green-600 font-black active:scale-95 transition-all">
            ✓ Biết rồi
          </button>
        </div>
      )}
    </div>
  );
};

const PictureMatchRound = ({ round, onAnswer, disabled }) => {
  const [selected, setSelected] = useState(null);
  const d = round.picture_match;
  const pick = (opt) => {
    if (disabled || selected) return;
    setSelected(opt);
    onAnswer(opt.correct);
  };
  return (
    <div className="space-y-4">
      <div className="bg-cyan-50 rounded-2xl p-5 text-center">
        <p className="text-xs font-bold text-cyan-500 uppercase mb-1">Chọn từ đúng</p>
        <p className="text-2xl font-black text-slate-800">{round.flashcard.name_vi}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {d.options.map((opt, i) => {
          let cls = 'bg-white border-2 border-slate-200';
          if (selected === opt) cls = opt.correct ? 'bg-green-50 border-2 border-green-400' : 'bg-red-50 border-2 border-red-400';
          else if (selected && opt.correct) cls = 'bg-green-50 border-2 border-green-300';
          return (
            <button key={i} onClick={() => pick(opt)} disabled={disabled || !!selected}
              className={`${cls} rounded-xl p-4 text-center font-bold text-slate-800 text-lg transition-all active:scale-95`}>
              {opt.word}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const WordScrambleRound = ({ round, onAnswer, disabled }) => {
  const [picked, setPicked] = useState([]);
  const [usedKeys, setUsedKeys] = useState(new Set());
  const [submitted, setSubmitted] = useState(false);
  const d = round.word_scramble;
  const tiles = d.scrambled.map((ch, i) => ({ ch, key: `${ch}-${i}` }));
  const typed = picked.map(p => p.ch).join('');

  const pickLetter = (ch, key) => {
    if (disabled || submitted || usedKeys.has(key)) return;
    setPicked(prev => [...prev, { ch, key }]);
    setUsedKeys(prev => new Set(prev).add(key));
  };
  const removeLast = () => {
    if (submitted || picked.length === 0) return;
    const last = picked[picked.length - 1];
    setPicked(prev => prev.slice(0, -1));
    setUsedKeys(prev => { const s = new Set(prev); s.delete(last.key); return s; });
  };
  const submit = () => {
    if (submitted || !typed) return;
    setSubmitted(true);
    // Scrambled tiles don't include spaces — compare letter-only
    const answerNoSpace = d.word.toLowerCase().replace(/\s+/g, '');
    const correct = typed.toLowerCase() === answerNoSpace;
    onAnswer(correct);
  };

  return (
    <div className="space-y-4">
      <div className="bg-violet-50 rounded-2xl p-4 text-center">
        <p className="text-xs font-bold text-violet-500 uppercase mb-1">Đánh vần từ</p>
        <p className="text-xl font-black text-slate-800">{d.hint_vi}</p>
      </div>
      <div className="bg-white rounded-xl p-3 border-2 border-dashed border-violet-200 min-h-[52px] flex items-center gap-1.5 flex-wrap justify-center">
        {picked.length === 0 && <span className="text-violet-300 text-sm">Tap chữ bên dưới</span>}
        {picked.map((p, i) => (
          <span key={p.key} className="w-9 h-10 rounded-lg border-2 border-violet-300 bg-violet-50 flex items-center justify-center text-lg font-black uppercase">
            {p.ch}
          </span>
        ))}
        {picked.length > 0 && !submitted && (
          <button onClick={removeLast} className="w-8 h-8 rounded-lg bg-slate-200 text-slate-500 flex items-center justify-center">
            <span className="material-symbols-outlined text-sm">backspace</span>
          </button>
        )}
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {tiles.map(t => (
          <button key={t.key} onClick={() => pickLetter(t.ch, t.key)} disabled={disabled || submitted || usedKeys.has(t.key)}
            className={`w-10 h-11 rounded-xl text-lg font-black uppercase transition-all
              ${usedKeys.has(t.key) ? 'bg-slate-100 text-slate-300 scale-90' : 'bg-white border-2 border-slate-300 text-slate-700 shadow-sm hover:border-violet-400 active:scale-95'}`}>
            {t.ch}
          </button>
        ))}
      </div>
      {!submitted && typed && (
        <button onClick={submit} className="w-full py-3 rounded-xl text-white font-black shadow-md active:scale-95 transition-all"
          style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>
          Kiểm tra
        </button>
      )}
    </div>
  );
};

const SpeedQuizRound = ({ round, onAnswer, disabled }) => {
  const [selected, setSelected] = useState(null);
  const [timer, setTimer] = useState(6);
  const d = round.speed_quiz;
  const timerRef = React.useRef(null);

  React.useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); onAnswer(false); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  const pick = (i) => {
    if (disabled || selected !== null) return;
    clearInterval(timerRef.current);
    setSelected(i);
    onAnswer(i === d.correct_index);
  };

  const timerColor = timer <= 2 ? '#ef4444' : timer <= 4 ? '#f59e0b' : '#22c55e';

  return (
    <div className="space-y-4">
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${(timer / 6) * 100}%`, background: timerColor }} />
      </div>
      <div className="bg-white rounded-2xl p-5 text-center shadow-sm">
        <p className="text-xs font-bold text-red-400 uppercase mb-1">⚡ Speed Quiz — {timer}s</p>
        <p className="text-xl font-black text-slate-800">{d.question}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {d.options.map((opt, i) => {
          let cls = 'bg-white border-2 border-slate-200';
          if (selected === i) cls = i === d.correct_index ? 'bg-green-50 border-2 border-green-400' : 'bg-red-50 border-2 border-red-400';
          else if (selected !== null && i === d.correct_index) cls = 'bg-green-50 border-2 border-green-300';
          return (
            <button key={i} onClick={() => pick(i)} disabled={disabled || selected !== null}
              className={`${cls} rounded-xl p-3 text-center font-bold text-slate-700 transition-all active:scale-95`}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LearningSession;
